import { forwardRef, useId, useState } from 'react';
import { Icon, type IconName } from '@/components/brand/Icon';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

interface FieldShellProps {
  label: string;
  hideLabel?: boolean | undefined;
  helper?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  optionalLabel?: string | undefined;
  className?: string | undefined;
  children: (ids: {
    inputId: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => React.ReactNode;
}

export function FieldShell({
  label,
  hideLabel,
  helper,
  error,
  required,
  optionalLabel,
  className,
  children,
}: FieldShellProps): React.JSX.Element {
  const { t } = useI18n();
  const inputId = useId();
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const describedBy =
    [error ? errorId : null, helper ? helperId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <label
        htmlFor={inputId}
        className={cn('text-label font-semibold text-primary', hideLabel && 'sr-only')}
      >
        {label}
        {!required && optionalLabel && (
          <span className="ml-1.5 font-normal text-tertiary">({optionalLabel})</span>
        )}
      </label>

      {children({ inputId, describedBy, invalid: Boolean(error) })}

      {error ? (
        <p id={errorId} className="flex items-start gap-1 text-caption text-danger-fg" role="alert">
          <Icon name="alert-triangle" size={14} className="mt-px" />
          <span>{t(error)}</span>
        </p>
      ) : (
        helper && (
          <p id={helperId} className="text-caption text-secondary">
            {helper}
          </p>
        )
      )}
    </div>
  );
}

const CONTROL_BASE =
  'w-full bg-surface text-body text-primary placeholder:text-placeholder ' +
  'border border-border-default rounded-sm px-4 ' +
  'transition-[border-color,box-shadow] duration-150 ' +
  'focus:border-action focus:outline-none focus:ring-2 focus:ring-action/20 ' +
  'disabled:bg-subtle disabled:text-tertiary disabled:cursor-not-allowed';

const INVALID = 'border-danger-border focus:border-danger-fg focus:ring-danger-fg/20';

type InputProps = {
  label: string;
  hideLabel?: boolean | undefined;
  helper?: string | undefined;
  error?: string | undefined;
  optionalLabel?: string | undefined;
  leadingIcon?: IconName | undefined;
  containerClassName?: string | undefined;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hideLabel,
    helper,
    error,
    optionalLabel,
    leadingIcon,
    containerClassName,
    className,
    required,
    ...rest
  },
  ref,
) {
  return (
    <FieldShell
      label={label}
      hideLabel={hideLabel}
      helper={helper}
      error={error}
      required={required}
      optionalLabel={optionalLabel}
      className={containerClassName}
    >
      {({ inputId, describedBy, invalid }) => (
        <div className="relative">
          {leadingIcon && (
            <Icon
              name={leadingIcon}
              size={18}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-placeholder"
            />
          )}
          <input
            ref={ref}
            id={inputId}
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={cn(
              CONTROL_BASE,
              'h-12',
              leadingIcon && 'pl-11',
              invalid && INVALID,
              className,
            )}
            {...rest}
          />
        </div>
      )}
    </FieldShell>
  );
});

type PasswordProps = Omit<InputProps, 'type'>;

export const PasswordInput = forwardRef<HTMLInputElement, PasswordProps>(function PasswordInput(
  { label, helper, error, containerClassName, className, required, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);

  return (
    <FieldShell
      label={label}
      helper={helper}
      error={error}
      required={required}
      className={containerClassName}
    >
      {({ inputId, describedBy, invalid }) => (
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={visible ? 'text' : 'password'}
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={cn(CONTROL_BASE, 'h-12 pr-12', invalid && INVALID, className)}
            {...rest}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-xs text-tertiary hover:text-primary"
          >
            <Icon name={visible ? 'eye-off' : 'eye'} size={18} />
          </button>
        </div>
      )}
    </FieldShell>
  );
});

type SelectProps = {
  label: string;
  hideLabel?: boolean | undefined;
  helper?: string | undefined;
  error?: string | undefined;
  optionalLabel?: string | undefined;
  containerClassName?: string | undefined;
  children: React.ReactNode;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hideLabel,
    helper,
    error,
    optionalLabel,
    containerClassName,
    className,
    required,
    children,
    ...rest
  },
  ref,
) {
  return (
    <FieldShell
      label={label}
      hideLabel={hideLabel}
      helper={helper}
      error={error}
      required={required}
      optionalLabel={optionalLabel}
      className={containerClassName}
    >
      {({ inputId, describedBy, invalid }) => (
        <div className="relative">
          <select
            ref={ref}
            id={inputId}
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={cn(
              CONTROL_BASE,
              'h-12 cursor-pointer appearance-none pr-11',
              invalid && INVALID,
              className,
            )}
            {...rest}
          >
            {children}
          </select>
          <Icon
            name="chevron-down"
            size={18}
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-tertiary"
          />
        </div>
      )}
    </FieldShell>
  );
});

type TextareaProps = {
  label: string;
  helper?: string | undefined;
  error?: string | undefined;
  optionalLabel?: string | undefined;
  containerClassName?: string | undefined;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, helper, error, optionalLabel, containerClassName, className, required, ...rest },
  ref,
) {
  return (
    <FieldShell
      label={label}
      helper={helper}
      error={error}
      required={required}
      optionalLabel={optionalLabel}
      className={containerClassName}
    >
      {({ inputId, describedBy, invalid }) => (
        <textarea
          ref={ref}
          id={inputId}
          required={required}
          rows={4}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(CONTROL_BASE, 'resize-y py-3', invalid && INVALID, className)}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

type CheckboxProps = {
  label: React.ReactNode;
  error?: string | undefined;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, error, className, ...rest },
  ref,
) {
  const { t } = useI18n();
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-3">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'mt-0.5 size-5 shrink-0 cursor-pointer rounded-xs border border-border-default',
            'accent-action',
            error && 'border-danger-border',
            className,
          )}
          {...rest}
        />
        <label htmlFor={id} className="cursor-pointer text-body-sm text-secondary">
          {label}
        </label>
      </div>
      {error && (
        <p id={errorId} className="flex items-start gap-1 text-caption text-danger-fg" role="alert">
          <Icon name="alert-triangle" size={14} className="mt-px" />
          <span>{t(error)}</span>
        </p>
      )}
    </div>
  );
});

type ToggleProps = {
  label: React.ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean | undefined;
  className?: string | undefined;
};

export function Toggle({
  label,
  checked,
  onCheckedChange,
  disabled,
  className,
}: ToggleProps): React.JSX.Element {
  const id = useId();

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="relative inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onCheckedChange(event.target.checked)}
          className="peer absolute inset-0 z-10 m-0 cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none flex h-6.5 w-11 items-center rounded-full transition-colors duration-150',
            'peer-focus-visible:shadow-[0_0_0_2px_var(--color-canvas),0_0_0_4px_var(--color-action)]',
            checked ? 'bg-action' : 'bg-muted',
            disabled && 'opacity-45',
          )}
        >
          <span
            className={cn(
              'size-5 rounded-full bg-surface shadow-card transition-transform duration-150',
              checked ? 'translate-x-[21px]' : 'translate-x-[3px]',
            )}
          />
        </span>
      </span>

      <label
        htmlFor={id}
        className={cn(
          'text-body-sm font-medium text-primary',
          disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer',
        )}
      >
        {label}
      </label>
    </div>
  );
}

type PhoneInputProps = Omit<InputProps, 'type' | 'leadingIcon'> & { dialCode?: string };

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    label,
    hideLabel,
    helper,
    error,
    optionalLabel,
    containerClassName,
    className,
    required,
    dialCode = '+994',
    ...rest
  },
  ref,
) {
  return (
    <FieldShell
      label={label}
      hideLabel={hideLabel}
      helper={helper}
      error={error}
      required={required}
      optionalLabel={optionalLabel}
      className={containerClassName}
    >
      {({ inputId, describedBy, invalid }) => (
        <div
          className={cn(
            'flex h-12 items-center gap-2 rounded-sm border border-border-default bg-surface px-4',
            'focus-within:border-action focus-within:ring-2 focus-within:ring-action/20',
            invalid && 'border-danger-border focus-within:border-danger-fg',
          )}
        >
          <span aria-hidden="true" className="text-body font-medium text-primary">
            {dialCode}
          </span>
          <span aria-hidden="true" className="h-5 w-px bg-border-default" />
          <input
            ref={ref}
            id={inputId}
            type="tel"
            inputMode="tel"
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-body text-primary outline-none placeholder:text-placeholder',
              className,
            )}
            {...rest}
          />
        </div>
      )}
    </FieldShell>
  );
});

export function PasswordStrength({
  value,
  labels,
  className,
}: {
  value: string;
  labels: [string, string, string, string];
  className?: string | undefined;
}): React.JSX.Element | null {
  if (!value) return null;

  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value) || /[A-Z]/.test(value)) score += 1;
  score = Math.max(1, score);

  const tone =
    score <= 1 ? 'bg-danger-solid' : score === 2 ? 'bg-warning-solid' : 'bg-success-solid';
  const text = score <= 1 ? 'text-danger-fg' : score === 2 ? 'text-warning-fg' : 'text-success-fg';

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex gap-1.5" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={cn('h-1 flex-1 rounded-[2px]', i < score ? tone : 'bg-muted')} />
        ))}
      </div>
      <p className={cn('text-caption font-medium', text)} aria-live="polite">
        {labels[score - 1]}
      </p>
    </div>
  );
}
